import React, { type FunctionComponent, useState } from 'react'
import { type FormContextValues } from 'react-hook-form'
import { SingleDatePicker } from 'react-dates'
import { type Moment } from 'moment'
import styled from '@emotion/styled'
import { ChevronDownIcon } from '@instacart/ids-core'
import moment from 'moment/moment'
import { type PartnerPickRecurringShiftsFormData } from './recurringShift.types'
import { RecurringShiftsRadio } from './confirmation-modal/RecurringShiftsRadio'
import { FormRow } from './ShiftDetailsForm.styles'

interface ShiftEndDateControlsProps {
  register: FormContextValues<PartnerPickRecurringShiftsFormData>['register']
  setValue: FormContextValues<PartnerPickRecurringShiftsFormData>['setValue']
  getValues: FormContextValues<PartnerPickRecurringShiftsFormData>['getValues']
  date: string
}
/* eslint-disable @emotion/syntax-preference */
const EndDateWrapper = styled.div`
  flex-direction: row-reverse;
  display: flex;

  .SingleDatePickerInput {
    border: none;
    border-radius: 8px;
    background-color: #f6f7f8;
    margin-bottom: 20px;
  }

  .DateInput {
    border-radius: 8px;
  }

  .DateInput__small {
    width: 120px;
  }

  .DateInput_input {
    height: 35px;
    padding: 8px 10px;
    text-align: left;
    font-size: 15px;
    font-weight: 600;
    line-height: 22px;
    cursor: pointer;
    border: none;
    border-radius: 8px;
    background-color: #f6f7f8;
  }

  .SingleDatePickerInput_calendarIcon {
    padding: 6px 0 0 0;
    margin: 0 10px 0 0;
    outline: none;
  }
`

const StyledRadioWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
`
/* eslint-enable @emotion/syntax-preference */

function getDefaultRecurEndDate(currentDate: string) {
  return moment(currentDate).add(7, 'd')
}

export const ShiftEndDateControls: FunctionComponent<ShiftEndDateControlsProps> = ({
  setValue,
  getValues,
  date: currentDate,
}) => {
  // Focused state for single date picker
  const [focused, setFocused] = useState(null)

  const defaultEndDate = getValues('recurEndDate')
    ? moment(getValues('recurEndDate'))
    : getDefaultRecurEndDate(currentDate)

  const [endDate, setEndDate] = useState<Moment>(defaultEndDate)

  const [hasEndDate, setHasEndDate] = useState(() => {
    const currentEndDate = getValues('recurEndDate')
    return currentEndDate ? 'true' : 'false'
  })

  const onEndDateChange = (endDate?: Moment) => {
    setValue('recurEndDate', endDate?.utc(), true)
    if (endDate) setEndDate(endDate)
  }

  const handleHasEndDateChange = () => {
    setHasEndDate('true')
    onEndDateChange(endDate)
  }

  const handleNoEndDateChange = () => {
    setHasEndDate('false')
    onEndDateChange()
  }

  return (
    <React.Fragment>
      <FormRow>
        <StyledRadioWrapper>
          <RecurringShiftsRadio
            labelMessageId={'shiftsDomain.prompt.noEndDate'}
            selectedValue={hasEndDate}
            identifier={'hasEndDate'}
            value="false"
            onChange={handleNoEndDateChange}
          />
          <RecurringShiftsRadio
            labelMessageId={'shiftsDomain.prompt.endsOn'}
            selectedValue={hasEndDate}
            identifier={'hasEndDate'}
            value="true"
            onChange={handleHasEndDateChange}
          />
        </StyledRadioWrapper>
      </FormRow>

      {hasEndDate === 'true' && (
        <EndDateWrapper>
          <SingleDatePicker
            small
            id="shiftDetailsEndDatePicker"
            numberOfMonths={1}
            displayFormat={() => 'DD MMM, YYYY'} // "13 Apr, 2021"
            required={hasEndDate === 'true'}
            hideKeyboardShortcutsPanel
            inputIconPosition="after"
            date={endDate}
            onDateChange={newDates => onEndDateChange(newDates)}
            focused={focused}
            onFocusChange={({ focused }) => setFocused(focused)}
            customInputIcon={<ChevronDownIcon size={20} color="systemGrayscale30" />}
            isOutsideRange={day => moment(currentDate) > day}
          />
        </EndDateWrapper>
      )}
    </React.Fragment>
  )
}
